import { useState, useRef, useEffect } from 'react';
import EverTrue from 'app';
import _ from 'underscore';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { getAddresses } from 'mixins/address-mixin';
import { Loading } from '@evertrue/et-components';
import ProfileCard from '../components/profile-card';
import CareerEnrichment from 'apps/profile/components/enrichment/career-enrichment';
import SocialEnrichment from '../components/enrichment/social-enrichment';
import OtherDetailsCard from '../components/enrichment/other-details-card';
import AddressSlideshow from '../components/enrichment/address-slideshow';
import PhilanthropicFinancialSignals from '../components/enrichment/philanthropic-financial-signals';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';

import { gqlCall } from 'entities/helpers/gqlCall';
import { usePromiseResolver } from '@evertrue/promise-resolver';
import useDonorSearchSummary from 'hooks/use-donor-search-summary';
import getProfileIdOverride from 'hooks/donorsearch-profile-overrides';

const gql = String.raw;

const QUERY = gql`
  query ($id: BigInt!) {
    contact(id: $id) {
      enrichment {
        enrichment_attributes(include_rejected: true) {
          id
          name_full
          value: name_full
          rejected
          created_at
          updated_at
          confidence_score
          source
          weight
        }
        addresses {
          address_1
          address_2
          address_3
          city
          state_prov
          country
          postal_code
          type
          source
          updated_at
        }
        phones {
          type
          phone
        }
        emails {
          type
          email
        }
        employments(include_rejected: true) {
          id
          status
          start_day
          start_month
          start_year
          end_day
          end_month
          end_year
          title
          company_name
          industry
          created_at
          updated_at
          rejected
          source
          confidence_score
          revenue_range
          size_range
          category
          weight
        }
        employment_events {
          id
          type
          confidence_score
          source
          recorded_at
          updated_at
        }
        contact_interests(include_rejected: true) {
          id
          interest_id
          interest_name
          confidence_score
          type
          updated_at
          rejected
        }
        social_profiles(include_rejected: true) {
          id
          type
          username
          bio
          url
          image_url
          followers
          following
          created_at
          updated_at
          confidence_score
          source
          rejected
          weight
        }
        wealth_attributes(include_rejected: true) {
          id
          net_worth
          net_worth_last_updated
          updated_at
          source
          rejected
        }
        wealth_indicators(include_rejected: true) {
          id
          type
          updated_at
          rejected
        }
        wealth_physical_assets(include_rejected: true) {
          id
          type
          updated_at
          rejected
        }
        contact_locations(include_rejected: true) {
          id
          city
          state_code
          state: state_code
          metro_area
          metro: metro_area
          country
          created_at
          updated_at
          rejected
          confidence_score
          source
          weight
        }
        wealth_life_events {
          id
          type
          updated_at
          created_at
        }
        donorsearch_scores_summary {
          donorsearch_profile_id
          created_at
          updated_at
          ds_rating
          quality_score
          pgid_rating
          rfm_score
          total_gift_amount
          total_gift_count
          likely_giving_amount
          likely_giving_count
          foundation_board_member
          foundation_income
          grank_seeking_board_member
          political_giving_count
          political_giving_amount
          likely_political_giving_count
          likely_political_giving_amount
          largest_gift_upper_estimate
          largest_gift_lower_estimate
          largest_gift_org_name
          real_estate_value
          real_estate_count
          real_estate_in_trust
          number_states_with_real_estate
          sec_stock_value
          sec_insider
          business_revenue
          business_affiliation
          estimated_capacity
          annual_fund_likelihood
          major_gift_likelihood
          planned_gift_likelihood
        }
        donorsearch_ai_scores {
          id
          type
          score_value
          score_label
          created_at
          updated_at
        }
      }
      et_score {
        dds_score
      }
    }
  }
`;

// This is for normalizing the FullContact data based on our contacts addresses model
// So I don't need to re-write a bunch of helper logic / lower level components
const formatAddresses = (addresses, contact) => {
  const contactAddresses = getAddresses(contact);
  const fullContactAddresses = addresses.map((address) => {
    return _.mapObject(address, (property, key) => {
      if (key === 'postal_code') {
        key = 'zip_code';
      } else if (key === 'state_prov') {
        key = 'state';
      }
      return { name: key, value: property };
    });
  });

  return contactAddresses.concat(fullContactAddresses);
};

const requestWithTracking = (id) => {
  return gqlCall(QUERY, { id: id }).then((results) => {
    const enrichmentData = get(results, 'data.contact.enrichment');
    const availableDataKeys = _.compactMap(_.omit(enrichmentData, 'id'), (value, key) => {
      if (!_.isEmpty(value)) {
        return key;
      }
    });
    EverTrue.track.set('enrichment_action', {
      type: 'view',
      contact_id: id,
      matched: !_.isEmpty(availableDataKeys),
      available_data: availableDataKeys,
    });
    return results;
  });
};

const ProfileEnrichmentController = ({ contact = {} }) => {
  const { flagEnabled: hasDonorsearch } = useFeatureFlag('donorsearch');
  const { flagEnabled: hasDonorsearchApi } = useFeatureFlag('donorsearchApi');
  const [shouldFetch, setShouldFetch] = useState(true);
  const [data] = usePromiseResolver(() => requestWithTracking(contact.id), [contact.id, shouldFetch]);
  const enrichmentData = get(data, 'data.contact.enrichment');
  const dpsScore = get(data, 'data.contact.et_score.dds_score') ?? null;

  // Cache the last valid enrichmentData to keep components mounted during refetch
  const cachedEnrichmentData = useRef(null);
  useEffect(() => {
    if (enrichmentData) {
      cachedEnrichmentData.current = enrichmentData;
    }
  }, [enrichmentData]);

  // Use cached data during refetch to prevent unmounting components
  const displayData = enrichmentData || cachedEnrichmentData.current;

  // Extract DonorSearch profile ID from GraphQL response
  const gqlProfileId = displayData?.donorsearch_scores_summary?.donorsearch_profile_id;

  // Override profile IDs for specific test contacts
  const profileIdOverride = getProfileIdOverride(contact.id);
  const donorsearchProfileId = profileIdOverride || gqlProfileId;

  // Determine if we can use DonorSearch API (all conditions must be true)
  const canUseDonorsearchApi = hasDonorsearch && hasDonorsearchApi && !!donorsearchProfileId;

  // Fetch high/low quality count breakdowns from /summary API
  const { data: summaryCounts, loading: summaryLoading } = useDonorSearchSummary(
    canUseDonorsearchApi ? contact.id : null,
    canUseDonorsearchApi ? donorsearchProfileId : null
  );

  const philInterests =
    displayData && displayData.contact_interests
      ? _.filter(displayData.contact_interests, (interest) => interest.type === 'philanthropic')
      : null;

  return (
    <div className="profile--enrichment">
      {!displayData ? (
        <Loading position="center" />
      ) : (
        <>
          <ProfileCard className="profile--enrichment-philanthropic-signals">
            <PhilanthropicFinancialSignals
              wealthIndicators={displayData.wealth_indicators}
              philInterests={philInterests}
              contactAttributes={{ contactId: contact.id, contactName: contact.name }}
              onDelete={() => setShouldFetch(!shouldFetch)}
              donorSearchData={displayData.donorsearch_scores_summary || {}}
              donorsearchAiScores={displayData.donorsearch_ai_scores || []}
              summaryCounts={summaryCounts}
              summaryLoading={summaryLoading}
              hasEnrichmentAccess={hasDonorsearch}
              hasDonorsearchApi={hasDonorsearchApi}
              canUseDonorsearchApi={canUseDonorsearchApi}
              netWorths={displayData.wealth_attributes}
              donorsearchProfileId={canUseDonorsearchApi ? donorsearchProfileId : null}
              dpsScore={dpsScore}
            />
          </ProfileCard>
          <ProfileCard className="profile--enrichment-addresses">
            <AddressSlideshow addresses={formatAddresses(displayData.addresses, contact)} />
          </ProfileCard>
          <ProfileCard className="profile--enrichment-career">
            <CareerEnrichment
              contact={contact}
              careers={displayData.employments}
              onDelete={() => setShouldFetch(!shouldFetch)}
            />
          </ProfileCard>
          <ProfileCard className="profile--enrichment-social">
            <SocialEnrichment
              contact={contact}
              profiles={displayData.social_profiles}
              onDelete={() => setShouldFetch(!shouldFetch)}
            />
          </ProfileCard>
          <ProfileCard className="profile--enrichment-other-details">
            <OtherDetailsCard
              contact={contact}
              enrichmentData={{
                wealth_life_events: displayData.wealth_life_events,
                employment_events: displayData.employment_events,
                enrichment_attributes: displayData.enrichment_attributes,
                contact_locations: displayData.contact_locations,
              }}
              onDelete={() => setShouldFetch(!shouldFetch)}
            />
          </ProfileCard>
        </>
      )}
    </div>
  );
};

ProfileEnrichmentController.propTypes = {
  contact: PropTypes.object,
};

export default ProfileEnrichmentController;
